/**
 * Widget Context Provider
 * React Context for widget library state management
 * Simplified for on-the-fly widget generation (no persistence)
 */

import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import type {
  WidgetContextType,
  TemplateFilters,
  PreviewState,
  CustomizationState,
} from './types';
import type { WidgetTemplate } from '../types';
import { DEFAULT_TEMPLATE_FILTERS } from '../constants';

// Initial state values
const initialFilters: TemplateFilters = DEFAULT_TEMPLATE_FILTERS;

const initialPreview: PreviewState = {
  isOpen: false,
  deviceType: 'desktop',
  activeTemplate: null,
};

const initialCustomization: CustomizationState = {
  isDirty: false,
  currentStep: 0,
};


// Create context
const WidgetContext = createContext<WidgetContextType | null>(null);

/**
 * Widget Provider Component
 */
export function WidgetProvider({ children }: { children: ReactNode }) {
  // State
  const [filters, setFilters] = useState<TemplateFilters>(initialFilters);
  const [preview, setPreview] = useState<PreviewState>(initialPreview);
  const [customization, setCustomization] = useState<CustomizationState>(initialCustomization);
  const [selectedTemplate, setSelectedTemplate] = useState<WidgetTemplate | null>(null);
  const [viewMode, setViewModeState] = useState<'grid' | 'list'>('grid');

  // Filter actions
  const setFilter = useCallback(<K extends keyof TemplateFilters>(key: K, value: TemplateFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
  }, []);

  const setSearchQuery = useCallback((query: string) => {
    setFilters((prev) => ({ ...prev, search: query }));
  }, []);

  const setSortBy = useCallback((sortBy: 'popular' | 'newest' | 'rating' | 'name') => {
    setFilters((prev) => ({ ...prev, sortBy }));
  }, []);

  // View mode
  const setViewMode = useCallback((mode: 'grid' | 'list') => {
    setViewModeState(mode);
  }, []);

  // Preview actions
  const openPreview = useCallback((template: WidgetTemplate) => {
    setPreview({
      isOpen: true,
      deviceType: 'desktop',
      activeTemplate: template,
    });
  }, []);

  const closePreview = useCallback(() => {
    setPreview((prev) => ({
      ...prev,
      isOpen: false,
      activeTemplate: null,
    }));
  }, []);

  const setPreviewDevice = useCallback((device: 'mobile' | 'tablet' | 'desktop') => {
    setPreview((prev) => ({ ...prev, deviceType: device }));
  }, []);

  // Customization actions
  const selectTemplateForCustomization = useCallback((template: WidgetTemplate) => {
    setSelectedTemplate(template);
    setCustomization({
      isDirty: false,
      currentStep: 0,
    });
  }, []);

  const setCustomizationStep = useCallback((step: number) => {
    setCustomization((prev) => ({ ...prev, currentStep: step }));
  }, []);

  const markCustomizationClean = useCallback(() => {
    setCustomization((prev) => ({ ...prev, isDirty: false }));
  }, []);

  // Reset actions
  const resetCustomization = useCallback(() => {
    setCustomization(initialCustomization);
    setSelectedTemplate(null);
  }, []);

  const resetAll = useCallback(() => {
    setFilters(initialFilters);
    setPreview(initialPreview);
    setCustomization(initialCustomization);
    setSelectedTemplate(null);
    setViewModeState('grid');
  }, []);

  // Memoized context value
  const value = useMemo<WidgetContextType>(
    () => ({
      // State
      filters,
      preview,
      customization,
      selectedTemplate,
      viewMode,

      // Actions
      setFilter,
      resetFilters,
      setSearchQuery,
      setSortBy,
      setViewMode,
      openPreview,
      closePreview,
      setPreviewDevice,
      selectTemplateForCustomization,
      setCustomizationStep,
      markCustomizationClean,
      resetCustomization,
      resetAll,
    }),
    [
      filters,
      preview,
      customization,
      selectedTemplate,
      viewMode,
      setFilter,
      resetFilters,
      setSearchQuery,
      setSortBy,
      setViewMode,
      openPreview,
      closePreview,
      setPreviewDevice,
      selectTemplateForCustomization,
      setCustomizationStep,
      markCustomizationClean,
      resetCustomization,
      resetAll,
    ]
  );

  return <WidgetContext.Provider value={value}>{children}</WidgetContext.Provider>;
}

/**
 * Hook to use widget context
 */
export function useWidget(): WidgetContextType {
  const context = useContext(WidgetContext);
  if (!context) {
    throw new Error('useWidget must be used within a WidgetProvider');
  }
  return context;
}

/**
 * Selector hooks for specific state slices
 */
export function useWidgetFilters() {
  const { filters, setFilter, resetFilters, setSearchQuery, setSortBy, viewMode, setViewMode } = useWidget();
  return { filters, setFilter, resetFilters, setSearchQuery, setSortBy, viewMode, setViewMode };
}

export function useWidgetPreview() {
  const { preview, openPreview, closePreview, setPreviewDevice } = useWidget();
  return { preview, openPreview, closePreview, setPreviewDevice };
}

export function useWidgetCustomization() {
  const {
    customization,
    selectedTemplate,
    selectTemplateForCustomization,
    setCustomizationStep,
    markCustomizationClean,
    resetCustomization,
  } = useWidget();
  return {
    customization,
    selectedTemplate,
    selectTemplateForCustomization,
    setCustomizationStep,
    markCustomizationClean,
    resetCustomization,
  };
}

export default WidgetProvider;
